/**
 * lx-shell —— 应用外壳
 *
 * 四层纵向固定结构（对齐手绘草图，设计文档 3.1）：
 *   ┌─ 标题栏   (56px，常驻)
 *   ├─ 内容区   (flex:1，独立滚动，三页切换)
 *   ├─ AI 输入栏 (56px，常驻)
 *   └─ 导航栏   (56px，常驻)
 *
 * 覆盖层（会话详情、联系人表单等）压在内容区之上、输入栏之下，
 * 这样打开会话时输入栏与导航栏依然可用 —— 是「弹层」而不是「跳页」。
 *
 * 平台：仅移动端竖屏。桌面端居中限宽 430px，模拟手机画框（D4）。
 */

import { LxElement, define } from '@/core/base-element';
import { settingsRepo, applyTheme, applyFontScale, watchSystemTheme } from '@/settings/settings';
import type { TabKey } from '@/types/events';
import { uiStore, pushOverlay, type OverlayEntry } from '@/core/ui-store';
import { contactsRepo } from '@/data/repo.contacts';
import type { LxChatView } from '@/components/lx-chat-view';
import type { LxContactEditor } from '@/components/lx-contact-editor';
import type { LxDigestView } from '@/components/lx-digest-view';
import '@/components/lx-chat-view';
import '@/components/lx-contact-editor';
import '@/components/lx-digest-view';
import '@/components/lx-camera-view';
import '@/components/lx-debug-view';
import '@/components/lx-voice-view';
import '@/components/lx-toast';

/** 覆盖层种类 → 承载它的组件标签 */
const OVERLAY_TAG: Partial<Record<string, string>> = {
  chat: 'lx-chat-view',
  'contact-editor': 'lx-contact-editor',
  digest: 'lx-digest-view',
  camera: 'lx-camera-view',
  debug: 'lx-debug-view',
  voice: 'lx-voice-view',
};

export class LxShell extends LxElement {
  protected styles(): string {
    return `
      :host {
        display: block;
        height: 100dvh;
        width: 100%;
        overflow: hidden;
      }

      /* 桌面端手机画框：限宽 + 居中 + 两侧留白 */
      .frame {
        height: 100%;
        width: 100%;
        max-width: var(--layout-app-max-width);
        margin: 0 auto;
        display: grid;
        grid-template-rows: auto 1fr auto auto;
        background: var(--c-bg);
        position: relative;
        overflow: hidden;
      }

      @media (min-width: 460px) {
        .frame {
          box-shadow: var(--shadow-lg);
          border-left: 1px solid var(--c-border);
          border-right: 1px solid var(--c-border);
        }
      }

      .content {
        /* grid 的 1fr 轨道必须显式约束，否则内容会撑破轨道、
           导致子组件的 min-height:100% 失去参照、出现多余滚动条 */
        min-height: 0;
        position: relative;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        display: flex;
        flex-direction: column;
      }

      /* 页面组件撑满内容区。
         basis 用 auto 而非 0 —— 设置页这类内容可能超过一屏的页面需要
         按内容撑高、由 .content 提供滚动；而空态类页面内部已改为
         纯 flex 分配高度、不再依赖百分比，所以不会被反向撑大。
         flex-shrink 留 1，让刚好一屏的页面能被收缩到轨道高度。 */
      .content > * {
        flex: 1 1 auto;
        min-height: 0;
      }

      /* ---------- 覆盖层 ---------- */
      /* 铺满整个 frame。聊天视图自带的发送框会顶掉底部输入栏，
         表单类覆盖层则铺满全屏 —— 逐层的观感由组件自己决定。 */
      .overlay-layer {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: var(--z-overlay);
      }

      .overlay-layer > * {
        pointer-events: auto;
      }
    `;
  }

  /**
   * 外壳骨架 —— **只在首次挂载时渲染一次**。
   *
   * ⚠️ 为什么不像别的组件那样每次 update() 重建整棵树：
   *   标题栏、AI 输入栏、导航栏都是**常驻控件**。如果每次切页/弹层都
   *   `innerHTML = render()` 重建整棵子树，它们会被销毁后重建 ——
   *   于是组件实例上的所有临时状态全部归零：
   *     · AI 输入栏正在进行的请求（转圈图标消失）
   *     · 按住说话的手势（松手事件再也无人接收，录音卡死）
   *     · 输入框里正在编辑但尚未发送的草稿
   *   这些状态**不属于某一页**，不该被切页连累。
   *
   *   所以外壳改成「骨架建一次，之后只局部更新变化的部分」：
   *     · 页面区（.content）随 tab 变
   *     · 覆盖层区（.overlay-layer）随 overlays 变
   *     · 标题栏/输入栏/导航栏只把新值**写到属性**上，元素本身不动
   *
   *   组件内部要不要响应属性变化由组件自己决定（见 connectedCallback
   *   里对 attributeChangedCallback 的处理），外壳不越权。
   */
  protected render(): string {
    const { tab } = uiStore.get();
    return `
      <div class="frame">
        <lx-title-bar tab="${tab}"></lx-title-bar>
        <main class="content" role="main">
          ${this.renderPage(tab)}
        </main>
        <lx-ai-input></lx-ai-input>
        <lx-tab-bar tab="${tab}"></lx-tab-bar>
        <div class="overlay-layer"></div>
      </div>
    `;
  }

  /**
   * 局部刷新：只更新会变的那两块，其余常驻控件原地保留。
   *
   * 这是「骨架建一次」策略的配套方法，每次状态变化后调用。
   */
  private patch(): void {
    const { tab, overlays } = uiStore.get();

    // ① 页面区：整体替换（页面之间本来就没有共享状态）
    const content = this.$('.content');
    if (content) {
      const nextHTML = this.renderPage(tab);
      // 比对一下当前是哪一页，避免同页重复重建（例如仅弹层变化时）
      const current = content.firstElementChild?.tagName.toLowerCase() ?? '';
      const wanted = nextHTML.match(/<([a-z-]+)/)?.[1] ?? '';
      if (current !== wanted) {
        content.innerHTML = nextHTML;
        // 切页后滚动位置归零 + 淡入
        content.scrollTo({ top: 0 });
        content.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease-out' });
      }
    }

    // ② 常驻控件：只写属性，不重建元素
    const titleBar = this.$('lx-title-bar');
    if (titleBar && titleBar.getAttribute('tab') !== tab) titleBar.setAttribute('tab', tab);
    const tabBar = this.$('lx-tab-bar');
    if (tabBar && tabBar.getAttribute('tab') !== tab) tabBar.setAttribute('tab', tab);

    // ③ 覆盖层区：整体替换（层与层之间不复用 DOM，见原设计说明）
    const layer = this.$('.overlay-layer');
    if (layer) {
      const nextHTML = this.renderOverlays(overlays).replace(/^\s*<div class="overlay-layer">/, '').replace(/<\/div>\s*$/, '');
      // 只有真的变了才动手，避免每次 patch 都把浮层重建（会打断正在进行的录音）
      const wantedKind = overlays[overlays.length - 1]?.kind ?? '';
      if (layer.dataset.kind !== wantedKind) {
        layer.dataset.kind = wantedKind;
        layer.innerHTML = nextHTML;
        this.syncOverlays();
      }
    }
  }

  private renderPage(tab: TabKey): string {
    switch (tab) {
      case 'contacts':
        return `<lx-page-contacts></lx-page-contacts>`;
      case 'settings':
        return `<lx-page-settings></lx-page-settings>`;
      default:
        return `<lx-page-home></lx-page-home>`;
    }
  }

  /**
   * 渲染覆盖层栈。
   * 只保留栈顶那层的 DOM —— 相比"留 DOM 只隐藏"更省内存，
   * 代价是返回上一层时会重建组件（重新读库）。对本项目的层数（最多 2 层）完全可以接受。
   */
  private renderOverlays(overlays: OverlayEntry[]): string {
    const visible = overlays[overlays.length - 1];
    if (!visible) return '<div class="overlay-layer"></div>';

    const tag = OVERLAY_TAG[visible.kind];
    if (!tag) return '<div class="overlay-layer"></div>';

    return `
      <div class="overlay-layer">
        <${tag} id="ov-${visible.kind}"></${tag}>
      </div>
    `;
  }

  /**
   * 把当前覆盖层的信息推给对应组件。
   * 不在 render 里直接写属性，因为组件实例可能刚被创建 ——
   * 要在它挂载后再设值。
   */
  private syncOverlays(): void {
    const top = uiStore.get().overlays.at(-1);
    if (!top) return;

    if (top.kind === 'chat') {
      const view = this.$('lx-chat-view') as LxChatView | null;
      if (view && view.sessionId !== top.refId) view.sessionId = top.refId;
    }

    if (top.kind === 'contact-editor') {
      const editor = this.$('lx-contact-editor') as LxContactEditor | null;
      if (editor) void this.fillEditor(editor, top.refId);
    }

    if (top.kind === 'digest') {
      const view = this.$('lx-digest-view') as LxDigestView | null;
      if (view && view.digestId !== top.refId) view.digestId = top.refId;
    }
  }

  /** 编辑器需要的联系人是异步取的，取完再赋值（只赋一次） */
  private async fillEditor(editor: LxContactEditor, contactId: string): Promise<void> {
    if (editor.dataset.filled === '1') return;
    editor.dataset.filled = '1';
    editor.contact = contactId ? ((await contactsRepo.get(contactId)) ?? null) : null;
  }

  protected onMounted(): void {
    // 首次挂载后把骨架里的动态区域补齐。
    // onMounted 会在每次 update() 后触发，但本组件只在首帧调用 update()
    // （之后一律走 patch），所以这里等价于「初始化一次」。
    this.patch();
  }

  connectedCallback(): void {
    super.connectedCallback();

    // 初始化外观：主题 + 字号，都是「改一处、全局生效」
    const app = settingsRepo.getApp();
    applyTheme(app.theme);
    applyFontScale(app.fontScale);

    // 恢复上次停留的页面（跨会话记忆，放在 settings 层）
    if (app.lastTab && app.lastTab !== uiStore.get().tab) {
      uiStore.set({ tab: app.lastTab });
    }

    // auto 模式下跟随系统配色实时切换
    watchSystemTheme(() => {
      const cur = settingsRepo.getApp();
      if (cur.theme === 'auto') applyTheme('auto');
    });

    // 订阅页面切换
    this.subscribe('ui:tab-changed', (payload) => {
      uiStore.set({ tab: payload.tab });
      settingsRepo.setApp({ lastTab: payload.tab });
      this.patch();
    });

    // 覆盖层变化 → 局部刷新
    // ⚠️ 注意这里用 patch 而不是 update：update 会重建整棵 shadow DOM，
    //    连常驻的 lx-ai-input 一起销毁重来 —— 那正是
    //    「按住说话松手后卡死」「请求转圈图标消失」这两类 bug 的根源。
    this.subscribe('ui:overlay-push', () => this.patch());
    this.subscribe('ui:overlay-pop', () => this.patch());

    // 打开联系人编辑表单 —— 也走覆盖层栈，这样 Esc/返回逻辑统一
    this.subscribe('ui:contact-editor-open', (payload) => {
      pushOverlay({ kind: 'contact-editor', refId: payload.contactId ?? '' });
    });

    // uiStore 的直接变化（如 pushOverlay）不走事件总线，这里补一层订阅
    this._unsubUiStore = uiStore.subscribe((state, prev) => {
      if (state.overlays !== prev.overlays) this.patch();
    });
  }

  private _unsubUiStore: (() => void) | null = null;

  protected onUnmounted(): void {
    this._unsubUiStore?.();
    this._unsubUiStore = null;
  }
}

define('lx-shell', LxShell);
